/**
 * Store pour la gestion des événements du marronnier
 */

import { ref, computed } from 'vue';
import { defineStore } from 'pinia';
import axios from '@js/tools/axios';

export const useHorseChestnutEventStore = defineStore('horseChestnutEvent', (() => {

    // Suggestion proposée au client connecté
    const authCustomerSuggestion = ref(false); // La valeur est à false tant qu'elle n'a pas été chargée

    // State de la liste des événements du marronnier (total et events)
    const eventList = ref({});

    // State des événements chargés
    const events = ref([]);

    /**
     * Retourne la liste complète des événements chargés
     * @return array
     */
    const getEvents = computed(() => events.value);

    /**
     * Retourne les données de la liste des événements (total, events)
     * @return object
     */
    const getEventList = computed(() => eventList.value);

    /**
     * Retourne l'événement dont l'identifiant est en paramètre
     * @param int event
     * @return object|null
     */
    const getEventById = computed(() => {
        return ((eventId) => {
            const event = getEvents.value.filter((currentEvent) => currentEvent.id === eventId);
            return (event.at(0) ?? null);
        });
    });

    /**
     * Retourne la suggestion au client connecté
     * @return object|null
     */
    const getAuthCustomerSuggestion = computed(() => {
        if(! authCustomerSuggestion.value) {
            return null;
        }
        return getEventById.value(authCustomerSuggestion.value.id);
    });

    /**
     * Chargement de la suggestion du client connecté
     * @return Promise
     */
    const loadAuthCustomerSuggestion = (() => {

        const path = '/api/horse-chestnut/events/suggestion';

        const params = {
            sort: {
                order: 'publication_date_targeted',
                direction: 'desc'
            }
        };

        return axios.get(path, {
            params: params
        }).then((response) => {
            if(response && response.data && response.data.success) {
                const newEvent = response.data.horseChestnutEvent ?? null;
                authCustomerSuggestion.value = newEvent;

                if(newEvent !== null) {
                    updateEvent(newEvent);
                }

                return Promise.resolve();
            }
            return Promise.reject();
        });
    });

    /**
     * Chargement de la liste des événements du marronnier
     * @param object params Paramètres pour la requête Ajax
     * @return Promise
     */
    const loadEventList = ((params) => {

        const path = '/api/horse-chestnut/events';

        return axios.get(path, {
            params: params
        }).then((response) => {
            if(response && response.data && response.data.success && response.data.data) {

                if(params.offset === 0) {
                    resetEventList();
                }

                updateEventList(response.data.data);

                return Promise.resolve();
            }
            return Promise.reject();
        });

    });

    /**
     * Chargement des données complète de l'événement dont l'identifiant est en paramètre
     * @param int horseChestnutEventId
     * @return Promise
     */
    const loadHorseChestnutEvent = ((horseChestnutEventId) => {

        const path = '/api/horse-chestnut/events/:eventId'.replace(':eventId', horseChestnutEventId);

        return axios.get(path).then((response) => {
            if(response && response.data && response.data.success && response.data.horseChestnutEvent) {
                updateEvent(response.data.horseChestnutEvent);
                return Promise.resolve();
            }
            return Promise.reject();
        });
    });

    /**
     * Conversion de l'événement en paramètre en tâche pour l'utilisateur connecté
     * @param object horseChestnutEvent
     * @return Promise
     */
    const convertInTask = ((horseChestnutEvent) => {

        const path = '/api/horse-chestnut/events/:eventId/convert-in-task'.replace(':eventId', horseChestnutEvent.id);

        return axios.post(path).then((response) => {
            if(response && response.data && response.data.success && response.data.horseChestnutEvent) {
                updateEvent(response.data.horseChestnutEvent);
                return Promise.resolve();
            }
            return Promise.reject();
        });

    });

    /**
     * Mise à jour de la liste des événements du marronnier avec les données en paramètre
     * @return void
     */
    const updateEventList = ((data) => {

        if(eventList.value.total === undefined) {
            eventList.value = {
                total: data.total,
                events: data.items
            };

            data.items.forEach((event) => {
                updateEvent(event);
            });

        } else {
            eventList.value.total = data.total;

            const currentEvents = eventList.value.events;
            const newEvents = data.items.filter((item) => ! currentEvents.find((current) => current.id === item.id));
            eventList.value.events = [ ...currentEvents, ...newEvents ];

            newEvents.forEach((event) => {
                updateEvent(event);
            });
        }

    });

    /**
     * Mise à jour des données dans le store de l'événement en paramètre
     * @param object horseChestnutEvent
     * @return void
     */
    const updateEvent = ((horseChestnutEvent) => {

        const horseChestnutEventId = horseChestnutEvent.id;

        const index = events.value.findIndex((eventFromState) => eventFromState.id === horseChestnutEventId);
        if(index !== -1) {
            events.value[index] = { ...events.value[index], ...horseChestnutEvent };
        } else {
            events.value.push(horseChestnutEvent);
        }

        events.value = [ ...events.value ];

        // Synchronisation avec les éléments de la liste
        const listData = eventList.value;
        if(listData.total !== undefined) {
            const listEvents = listData.events || [];
            const indexInList = listEvents.findIndex((eventFromState) => eventFromState.id === horseChestnutEventId);
            if(indexInList !== -1) {
                listEvents[indexInList] = { ...listEvents[indexInList], ...horseChestnutEvent };
            } else {
                eventList.value.total += 1;
                listEvents.push(horseChestnutEvent);
            }

            eventList.value.events = [ ...listEvents ];
        }
    });

    /**
     * Mise à jour des données de la tâche de l'événement en paramètre
     * @param object horseChestnutEvent
     * @param object task
     * @return void
     */
    const updateEventTask = ((horseChestnutEvent, task) => {

        const newObject = { ...horseChestnutEvent, ...{
            convertingTask: task
        } };

        updateEvent(newObject);
    });

    /**
     * Réinitialisation de la liste des événements
     * @return void
     */
    const resetEventList = (() => {
        eventList.value = {};
    });

    /**
     * Réinitialisation des événements
     * @return void
     */
    const reset = (() => {
        eventList.value = {};
        events.value = [];
    });

    return {
        reset,
        getEvents,
        getEventList,
        getEventById,
        getAuthCustomerSuggestion,
        loadEventList,
        loadHorseChestnutEvent,
        loadAuthCustomerSuggestion,
        updateEventList,
        updateEvent,
        convertInTask,
        updateEventTask
    };
}));
